import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { ThemeColors } from '@tanium/theme-data';
import { Box } from '@tanium/react-box';
import { Button } from '@tanium/react-button';
import { Stack } from '@tanium/react-stack';
import { Well } from '@tanium/react-well';
import { ConfirmModal } from '@tanium/react-confirm-modal';
import { Notification } from '@tanium/react-persistent-notifications';
import { Shimmer } from '@tanium/react-shimmer';
import { Form, FormRow } from '@tanium/react-form';
import { FormTextInput } from '@tanium/react-form-text-input';
import { LargeBoldText, MediumText, SmallSecondaryText } from '../../TextStyles';
import { AsyncSuspense } from '../../components/AsyncSuspense';
import { useTenant } from '../../hooks/useTenant';
import { AsyncStatus } from '../../hooks/useAsync';
import { useAuthContext } from '../../auth';
import { isValidEmail } from '../../utilities/emailHelper';

interface ModalProps {
  isOpen: boolean;
  tenantId: string | undefined;
  currentAdminUser: string | undefined;
  onRequestClose: () => void;
}

interface AdminSettings {
  adminUsername: string;
  adminEmail: string;
  confirmAdminEmail: string;
}

const emptyAdminSettings = {
  adminUsername: '',
  adminEmail: '',
  confirmAdminEmail: '',
};

export const UpdateAdminModal: React.FC<ModalProps> = ({ isOpen, tenantId, currentAdminUser, onRequestClose }) => {
  const { t } = useTranslation();
  const { postAdminSettings } = useTenant();
  const { signOut } = useAuthContext();
  const [agreementAccepted, setAgreementAccepted] = useState<boolean>(false);
  const [adminSettings, setAdminSettings] = useState<AdminSettings>({ ...emptyAdminSettings });
  const [usernameError, setUsernameError] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [confirmEmailError, setConfirmEmailError] = useState<string>('');
  const [isFinalConfirmModalOpen, setIsFinalConfirmModalOpen] = useState<boolean>(false);
  const adminUsername = adminSettings.adminUsername.trim().toLowerCase();
  const adminEmail = adminSettings.adminEmail.trim().toLowerCase();
  const confirmAdminEmail = adminSettings.confirmAdminEmail.trim().toLowerCase();

  // Checks if there are any validation errors, and if admin settings are empty
  const hasValidationError = (): boolean => {
    return (
      !!(usernameError || emailError || confirmEmailError) ||
      !(adminUsername && adminEmail && confirmAdminEmail)
    );
  };

  const validateAdminUsername = () => {
    const prefixCheck = /^[a-z0-9.:/\-_]{1,40}@.*$/;
    const identifierCheck = /^[a-z0-9.:/\-_]{1,40}@(?!.*\.\.)(?:[a-z\d_\-+.]{3,120})\.mytanium\.com$/;

    if (!prefixCheck.test(adminUsername)) {
      setUsernameError(
        'Username must be of the format <prefix>@<identifer>.mytanium.com and the prefix may include letters, numbers, and these symbols: ., :, /, -, _.'
      );
    } else if (!identifierCheck.test(adminUsername)) {
      setUsernameError(
        'Username must be of the format <prefix>@<identifer>.mytanium.com and the identifer may include letters, numbers, periods, and hyphens.'
      );
    } else if (adminUsername.length > 128) {
      setUsernameError('Username cannot exceed 128 characters');
    } else {
      setUsernameError('');
    }
  };

  const validateAdminEmail = () => {
    if (adminEmail !== confirmAdminEmail) {
      setConfirmEmailError('Emails do not match');
    } else {
      setConfirmEmailError('');
    }
    if (!isValidEmail(adminEmail)) {
      setEmailError('Invalid email');
    } else {
      setEmailError('');
    }
  };

  const onAgreementClick = () => {
    setAgreementAccepted(true);
  };

  useEffect(() => {
    const logoutDelayMillis = 3 * 1000; // 3 seconds

    if (postAdminSettings.status === AsyncStatus.Success) {
      /* istanbul ignore next */
      setTimeout(() => {
        postAdminSettings.reset();
        setAdminSettings(emptyAdminSettings);
        onRequestClose();
        signOut();
      }, logoutDelayMillis);
    }
  }, [onRequestClose, postAdminSettings, signOut, t]);

  return (
    <ConfirmModal
      isOpen={isOpen}
      header={t('Change Root Admin')}
      confirmLabel={t('Change Root Admin')}
      cancelLabel={t('Cancel')}
      onConfirm={() => {
        setIsFinalConfirmModalOpen(true);
      }}
      isConfirmDisabled={
        postAdminSettings.status === AsyncStatus.Pending ||
        postAdminSettings.status === AsyncStatus.Success ||
        hasValidationError() ||
        !agreementAccepted
      }
      onCancel={() => {
        setAdminSettings(emptyAdminSettings);
        setAgreementAccepted(false);
        onRequestClose();
        postAdminSettings.reset();
      }}
    >
      <Stack direction="column" itemSpacing={2}>
        <Box>
          <Stack justifyContent="left" direction="column" itemSpacing={2}>
            <Notification role="status" type="warning">
              {t('After the new user is created, the current user will be deleted and you will be logged out.')}
            </Notification>
            <Form
              initialValue={adminSettings}
              buttons={<></>}
              onValueChange={(e) => {
                setAdminSettings({ ...adminSettings, ...e.value });
              }}
            >
              <FormRow dataKey="adminUsername" label={t('Username')} required errors={usernameError}>
                <FormTextInput onBlur={() => validateAdminUsername()} />
                <br />
                <SmallSecondaryText>{'Username format is <user>@<identifier>.mytanium.com'}</SmallSecondaryText>
              </FormRow>
              <FormRow dataKey="adminEmail" label={t('Email Address')} required errors={emailError}>
                <FormTextInput onBlur={() => validateAdminEmail()} />
              </FormRow>
              <FormRow
                dataKey="confirmAdminEmail"
                label={t('Confirm Root Admin Email Address')}
                required
                errors={confirmEmailError}
              >
                <FormTextInput onBlur={() => validateAdminEmail()} />
              </FormRow>
              <Box borderRightRadius="8px" bg={ThemeColors.FillLevel2} height="100%" width="600px" alignItems="left">
                <Well>
                  <Stack justifyContent="left" direction="column" itemSpacing={1}>
                    <LargeBoldText>{t('Notice and Acknowledgement')}</LargeBoldText>
                    <Well>
                      <MediumText>
                        {t(
                          'By setting up an individual as a (primary) administrator, you acknowledge and agree that you are solely responsible for the selection and actions of the administrator. You understand that the administrator will have significant control over your Tanium account, including but not limited to access to sensitive information, the ability to make changes to account settings, the authority to manage other users and all installed endpoints and entity types.'
                        )}
                      </MediumText>
                    </Well>
                  </Stack>
                  <Box as={Stack} justifyContent="center" mt={1}>
                    <Button variant="primary" onClick={onAgreementClick} disabled={agreementAccepted}>
                      <Box width="300px"> {t('Agree')}</Box>
                    </Button>
                  </Box>
                </Well>
              </Box>
              <AsyncSuspense
                state={postAdminSettings}
                loading={
                  <Box mt={2} height={1}>
                    <Shimmer />
                  </Box>
                }
                failure={
                  <Box mt={2} mb={0}>
                    <Notification role="status" type="error">
                      {postAdminSettings.error?.displayMessage || t('Failed to create new root administrator')}
                    </Notification>
                  </Box>
                }
              >
                <Box mt={2} mb={0}>
                  <Notification role="status" type="success">
                    {t(
                      'The new user has been created and an email has been sent with login instructions. The user you are logged in with has been deleted, and you will be logged out in 10 seconds.'
                    )}
                  </Notification>
                </Box>
              </AsyncSuspense>
            </Form>
          </Stack>
        </Box>
      </Stack>
      <ConfirmModal
        isOpen={isFinalConfirmModalOpen}
        header={t('Confirm Root Admin Creation and Deletion')}
        confirmLabel={t('Confirm')}
        cancelLabel={t('Cancel')}
        onConfirm={async () => {
          setIsFinalConfirmModalOpen(false);
          if (tenantId) {
            await postAdminSettings.execute(tenantId, adminUsername, adminEmail);
          }
        }}
        onCancel={() => setIsFinalConfirmModalOpen(false)}
      >
        <MediumText>
          <Trans
            i18nKey={
              'Are you sure you wish to create new root admin user newUsername? The current root admin user oldUsername will be deleted and you will be logged out after three seconds.'
            }
            values={{
              newUsername: adminUsername,
              oldUsername: currentAdminUser,
            }}
            components={{
              bold: <strong />,
            }}
          >
            Are you sure you wish to create new root admin user? The current root admin user will be deleted and you
            will be logged out after three seconds.
          </Trans>
        </MediumText>
      </ConfirmModal>
    </ConfirmModal>
  );
};
